import * as TYPES from '../../store/types'
import { http } from '../../plugins/http'
import registry from './definitions/_registry'

const state = {
  tours: [],
  progress: [],
  skipAutoStart: false,
  activeTourKey: null,
  activeStepIndex: 0,
  fetched: false,
}

/* eslint-disable no-param-reassign */
const mutations = {
  [TYPES.TOURS_SET_DATA](st, { tours, progress, skipAutoStart }) {
    st.tours = tours || []
    st.progress = progress || []
    st.skipAutoStart = !!skipAutoStart
  },
  [TYPES.TOURS_SET_PROGRESS](st, progress) {
    st.progress = progress || []
  },
  [TYPES.TOURS_SET_ACTIVE](st, { tourKey, stepIndex }) {
    st.activeTourKey = tourKey
    st.activeStepIndex = stepIndex || 0
  },
  [TYPES.TOURS_CLEAR_ACTIVE](st) {
    st.activeTourKey = null
    st.activeStepIndex = 0
  },
  [TYPES.TOURS_SET_FETCHED](st, fetched) {
    st.fetched = !!fetched
  },
}

// Merge a single returned progress row into state.progress (insert or replace by tourKey).
const mergeProgressRow = (currentProgress, row) => {
  if (!row || !row.tourKey) return currentProgress || []
  const list = (currentProgress || []).filter(n => n.tourKey !== row.tourKey)
  list.push(row)
  return list
}

const actions = {
  // Pulled once after user login (and on demand). Backend filters tours by audience for us.
  async fetchTours({ commit }) {
    try {
      const { data } = await http.get('v1/tours')
      commit(TYPES.TOURS_SET_DATA, {
        tours: data.tours,
        progress: data.progress,
        skipAutoStart: data.skipAutoStart,
      })
      commit(TYPES.TOURS_SET_FETCHED, true)
      return data
    } catch (err) {
      // Do not break login; just mark as fetched so welcome modal never opens.
      commit(TYPES.TOURS_SET_FETCHED, true)
      throw err
    }
  },

  async updateProgress({ commit, state }, { tourKey, stepIndex, status }) {
    const body = {
      tourKey,
      currentStepIndex: stepIndex == null ? 0 : stepIndex,
      status,
    }
    const { data } = await http.put('v1/tours/progress', body)
    if (data && data.progress) {
      commit(TYPES.TOURS_SET_PROGRESS, mergeProgressRow(state.progress, data.progress))
    }
    return data
  },

  async startTour({ commit, dispatch }, tourKey) {
    commit(TYPES.TOURS_SET_ACTIVE, { tourKey, stepIndex: 0 })
    return dispatch('updateProgress', { tourKey, stepIndex: 0, status: 'InProgress' })
  },

  async resumeTour({ commit, dispatch, state }, tourKey) {
    const row = (state.progress || []).find(n => n.tourKey === tourKey)
    const stepIndex = row && row.currentStepIndex >= 0 ? row.currentStepIndex : 0
    commit(TYPES.TOURS_SET_ACTIVE, { tourKey, stepIndex })
    return dispatch('updateProgress', { tourKey, stepIndex, status: 'InProgress' })
  },

  // User explicitly chose "Skip" from inside the runner.
  async skipTour({ commit, dispatch, state }, tourKey) {
    const stepIndex = state.activeStepIndex
    commit(TYPES.TOURS_CLEAR_ACTIVE)
    return dispatch('updateProgress', { tourKey, stepIndex, status: 'Skipped' })
  },

  // Last step reached and the user pressed Finish.
  async finishTour({ commit, dispatch, state }, tourKey) {
    const def = registry[tourKey]
    const stepCount = def && def.steps ? def.steps.length : (state.activeStepIndex + 1)
    commit(TYPES.TOURS_CLEAR_ACTIVE)
    return dispatch('updateProgress', { tourKey, stepIndex: stepCount - 1, status: 'Completed' })
  },

  // X (close) was pressed — preserve position, status stays InProgress so the user can resume later.
  async pauseTour({ commit, dispatch, state }, tourKey) {
    const stepIndex = state.activeStepIndex
    commit(TYPES.TOURS_CLEAR_ACTIVE)
    return dispatch('updateProgress', { tourKey, stepIndex, status: 'InProgress' })
  },

  async resetTour({ dispatch }, tourKey) {
    await http.delete(`v1/tours/progress/${encodeURIComponent(tourKey)}`)
    return dispatch('fetchTours')
  },

  async resetAllTours({ dispatch }) {
    await http.delete('v1/tours/progress')
    return dispatch('fetchTours')
  },

  // Admin resets another user's tours. No mutation of local state — the target isn't the current user.
  async adminResetUserTours(_, userId) {
    return http.put(`v1/users/${userId}/reset-tours`)
  },
}

const module = {
  namespaced: true,
  state,
  mutations,
  actions,
}

export default { module }
